/**
 * Pure derivations for the spectator overlay — heat phase, zone health,
 * status flag aggregation. Kept here (not inlined in JSX) so the math
 * is unit-testable in Node without mounting React.
 *
 * Mirrors the deterministic engine's constants where possible. The
 * visual-engine package intentionally doesn't import the Python engine
 * (or its TS port) so these are duplicated by intent — keep them in
 * sync with `server/mecharena/resolver.py` heat thresholds.
 */

import type { MechState, ZoneState } from "@pilot-protocol/replay-parser";

// Engine heat thresholds (resolver.py — HEAT_WARN / HEAT_DANGER /
// HEAT_SHUTDOWN). Inline to keep the determinism boundary.
export const HEAT_WARN = 20;
export const HEAT_DANGER = 25;
export const HEAT_SHUTDOWN = 30;
/** Visual cap for the heat bar — above this we just clamp at 100%. */
export const HEAT_BAR_MAX = 35;

export type HeatPhase = "safe" | "warn" | "danger" | "critical";

/** Discrete classification of a mech's current heat level. */
export function heatPhase(heat: number): HeatPhase {
  if (heat >= HEAT_SHUTDOWN) return "critical";
  if (heat >= HEAT_DANGER) return "danger";
  if (heat >= HEAT_WARN) return "warn";
  return "safe";
}

/** Heat as a 0..1 ratio of the visual bar's max. */
export function heatRatio(heat: number): number {
  return Math.max(0, Math.min(1, heat / HEAT_BAR_MAX));
}

export type ZoneHealth =
  | { state: "armor"; armorPct: number; armor: number; internal: number }
  | { state: "internal"; internalPct: number; armor: 0; internal: number }
  | { state: "destroyed"; armor: 0; internal: 0 };

/**
 * Engine zones reach 0 armor → `exposed=true`; then internal ticks
 * down; then destroyed. Classify the current display state. Pcts are
 * relative to the starting values the engine seeds (4/4 for non-head,
 * varying for head) — we don't have the starting values on hand, so
 * the consumer passes the max if it needs a true %. For the overlay
 * we keep absolute numbers + a state tag, and let the renderer pick
 * how to draw it.
 */
export function zoneHealth(z: ZoneState, maxArmor = 4, maxInternal = 4): ZoneHealth {
  if (z.internal <= 0 && z.armor <= 0) {
    return { state: "destroyed", armor: 0, internal: 0 };
  }
  if (z.exposed || z.armor <= 0) {
    return {
      state: "internal",
      internalPct: Math.max(0, Math.min(1, z.internal / maxInternal)),
      armor: 0,
      internal: z.internal,
    };
  }
  return {
    state: "armor",
    armorPct: Math.max(0, Math.min(1, z.armor / maxArmor)),
    armor: z.armor,
    internal: z.internal,
  };
}

export interface StatusFlags {
  shutdown: boolean;
  staggered: boolean;
  knockedDown: boolean;
  smoke: boolean;
  tagged: boolean;
  ecm: boolean;
  /** Number of locks currently held on this mech (0 = none). */
  targetLocks: number;
  scanned: boolean;
}

/** Pull the boolean / count fields out of a MechState in one place. */
export function statusFlagsOf(mech: MechState): StatusFlags {
  return {
    shutdown: mech.shutdown,
    staggered: mech.staggered,
    knockedDown: mech.knocked_down,
    smoke: mech.smoke_active,
    tagged: mech.tagged,
    ecm: mech.ecm_bubble_active,
    targetLocks: mech.target_lock_on,
    scanned: mech.scanned_this_turn,
  };
}

export interface CritCounts {
  engine: number;
  gyro: number;
  lLegActuator: number;
  rLegActuator: number;
  destroyedTools: number;
}

/** Crits + actuator damage, grouped for a compact panel display. */
export function critCountsOf(mech: MechState): CritCounts {
  return {
    engine: mech.engine_crits,
    gyro: mech.gyro_crits,
    lLegActuator: mech.l_leg_actuator_damage,
    rLegActuator: mech.r_leg_actuator_damage,
    destroyedTools: mech.destroyed_tools.length,
  };
}

/** True if the mech has any crit / actuator damage. */
export function hasAnyCrits(c: CritCounts): boolean {
  return (
    c.engine > 0 ||
    c.gyro > 0 ||
    c.lLegActuator > 0 ||
    c.rLegActuator > 0 ||
    c.destroyedTools > 0
  );
}
